import { createContext, useContext } from 'react';

/**
 * The selected element's refusals, reachable by any field that renders under it.
 *
 * Context rather than props because the alternative was measured and rejected:
 * threading `fieldErrors` and `onResolveField` by hand put the same two
 * parameters through six signatures across four files, and marking one more
 * field meant editing most of them. Nothing between the inspector and the field
 * has any use for them — they were pure carriage.
 *
 * The provider is the only thing that knows *which* element is selected; a field
 * only knows its own name, which is exactly the amount of knowledge it should
 * need.
 */
export const FieldErrorContext = createContext<{
  errors: Record<string, string>;
  onResolve?: (field: string) => void;
} | null>(null);

/**
 * One field's refusal, and the way to withdraw it.
 *
 * `resolve` is deliberately something a caller invokes rather than something
 * this hook does on its own: a commit handler is the only place that knows
 * whether an edit actually landed. Two of them refuse the value they are given
 * — a traversal limit that is not a positive integer, an identifier that
 * collides — and withdrawing a refusal about a value still in the document
 * would drop a problem that is still true.
 *
 * Safe outside a provider, where it reports no error and resolves nothing: the
 * inspectors render in tests and in the read-only panel without one.
 */
export function useFieldError(field?: string): {
  error?: string;
  resolve: () => void;
} {
  const context = useContext(FieldErrorContext);
  if (!field || !context) return { error: undefined, resolve: () => undefined };
  return {
    error: context.errors[field],
    resolve: () => context.onResolve?.(field),
  };
}
